import { useEffect } from 'react';

// Publishes the on-screen keyboard size to a CSS variable on :root:
//   --keyboard-lift  how far the bottom of the locked full-height app
//                    (--app-height) sits below the visible area
//
// On iOS the WebView resizes only at the END of the keyboard animation, and in
// the meantime iOS scrolls the page up to reveal a focused input near the
// bottom — causing an up-then-down flicker. To avoid that we PRE-LIFT bottom
// anchored UI the instant an input is focused (using the last measured keyboard
// height), so the input is already above where the keyboard will land and iOS
// has no reason to scroll. While focused we never dip below that pre-lift, so
// the composer holds steady through the whole animation.
const useVisualViewport = () => {
  useEffect(() => {
    const viewport = window.visualViewport;

    if (!viewport) {
      return undefined;
    }

    const root = document.documentElement;
    const STORAGE_KEY = 'tg-keyboard-lift';

    const readStoredLift = () => {
      try {
        return parseFloat(window.localStorage.getItem(STORAGE_KEY)) || 0;
      } catch {
        return 0;
      }
    };
    const storeLift = value => {
      try {
        window.localStorage.setItem(STORAGE_KEY, String(value));
      } catch {
        // localStorage may be unavailable (private mode) — pre-lift just falls
        // back to the estimate on the first focus of the session.
      }
    };

    // Seed from the last measured height persisted on a previous run so even the
    // first focus after reopening pre-lifts to the exact keyboard height.
    let lastLift = readStoredLift();
    let focusLift = 0; // pre-lift floor applied only while the keyboard opens
    let expectingOpen = false; // focused and waiting for iOS to report the keyboard

    // Only elements that actually summon the on-screen keyboard should trigger a
    // pre-lift. react-modal focuses its container <div> on open, and tapping the
    // overlay moves focus to non-input nodes — those must be ignored.
    const NON_TEXT_INPUT_TYPES = ['button', 'submit', 'reset', 'checkbox', 'radio', 'file', 'range', 'color', 'image', 'hidden'];
    const opensKeyboard = el => {
      if (!el) {
        return false;
      }
      if (el.tagName === 'TEXTAREA') {
        return true;
      }
      if (el.tagName === 'INPUT') {
        return !NON_TEXT_INPUT_TYPES.includes((el.getAttribute('type') || 'text').toLowerCase());
      }
      return el.isContentEditable === true;
    };

    const lockedHeight = () =>
      parseFloat(getComputedStyle(root).getPropertyValue('--app-height')) || window.innerHeight;

    const update = () => {
      const computed = Math.max(0, lockedHeight() - viewport.height);
      if (computed > 0 && computed !== lastLift) {
        lastLift = computed;
        storeLift(computed);
      }

      let lift;
      if (expectingOpen) {
        // Between focus and iOS reporting the keyboard size: hold the pre-lift
        // (and never dip below it) so the composer doesn't bounce.
        lift = Math.max(computed, focusLift);
        // Once the keyboard is actually reported, switch to live tracking so it
        // can also follow the keyboard back down to 0 when dismissed.
        if (computed > 0) {
          expectingOpen = false;
        }
      } else {
        // Keyboard present -> track it; keyboard gone (computed 0) -> rest at the
        // bottom, even if the input still holds focus (iOS keeps focus on hide).
        lift = computed;
      }

      root.style.setProperty('--keyboard-lift', `${lift}px`);
    };

    // Keep the document pinned to the top so the keyboard cannot push the page.
    const pinToTop = () => {
      if (window.scrollY !== 0 || window.pageYOffset !== 0) {
        window.scrollTo(0, 0);
      }
      if (document.scrollingElement && document.scrollingElement.scrollTop !== 0) {
        document.scrollingElement.scrollTop = 0;
      }
    };

    const handleViewportChange = () => {
      update();
      pinToTop();
    };

    const handleFocusIn = event => {
      if (!opensKeyboard(event.target)) {
        return;
      }
      expectingOpen = true;
      // Pre-lift before iOS measures the input: reuse the last measured keyboard
      // height, or slightly under-estimate on the very first focus so it settles
      // upward (never stranded too high) once the real size arrives.
      focusLift = lastLift || Math.round(lockedHeight() * 0.33);
      root.style.setProperty('--keyboard-lift', `${focusLift}px`);
      pinToTop();
    };

    const handleFocusOut = event => {
      if (!opensKeyboard(event.target)) {
        return;
      }
      expectingOpen = false;
      focusLift = 0;
      // Drop the lift immediately so the composer slides down WITH the closing
      // keyboard, instead of waiting for the (late) viewport resize event.
      root.style.setProperty('--keyboard-lift', '0px');
    };

    update();
    viewport.addEventListener('resize', handleViewportChange);
    viewport.addEventListener('scroll', handleViewportChange);
    window.addEventListener('scroll', pinToTop, { passive: true });
    document.addEventListener('focusin', handleFocusIn);
    document.addEventListener('focusout', handleFocusOut);

    return () => {
      viewport.removeEventListener('resize', handleViewportChange);
      viewport.removeEventListener('scroll', handleViewportChange);
      window.removeEventListener('scroll', pinToTop);
      document.removeEventListener('focusin', handleFocusIn);
      document.removeEventListener('focusout', handleFocusOut);
      root.style.removeProperty('--keyboard-lift');
    };
  }, []);
};

export default useVisualViewport;
